import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

const sanitizeInput = (input) => {
  if (typeof input === "string") {
    return input.replace(/[<>]/g, "").trim().substring(0, 1000);
  }
  return input;
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";

      if (
        !url.includes("/admin") &&
        !url.includes("/items") &&
        !url.includes("/lists") &&
        !url.includes("/categories") &&
        !url.includes("/progress") &&
        !url.includes("/pause-timer") &&
        !url.includes("/abort-timed") &&
        !url.includes("/update-mode")
      ) {
        localStorage.removeItem("token");
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

export const authAPI = {
  login: (credentials) =>
    api.post("/auth/login", {
      usernameOrEmail: sanitizeInput(credentials.usernameOrEmail),
      password: credentials.password,
    }),
  register: (userData) =>
    api.post("/auth/register", {
      username: sanitizeInput(userData.username),
      email: sanitizeInput(userData.email),
      password: userData.password,
    }),
};

export const userAPI = {
  getCurrentUser: () => api.get("/user/me"),
  updateUser: (userData) =>
    api.put("/user/update", {
      username: userData.username
        ? sanitizeInput(userData.username)
        : undefined,
      email: userData.email ? sanitizeInput(userData.email) : undefined,
    }),
  changePassword: (passwordData) =>
    api.put("/user/change-password", passwordData),
  deleteAccount: () => api.delete("/user/delete"),
};

export const categoryAPI = {
  getAllCategories: () => api.get("/categories/public"),
  getCategoryById: (id) => api.get(`/categories/public/${id}`),
  getCategoryBySlug: (slug) => api.get(`/categories/public/slug/${slug}`),
  createCategory: (categoryData) =>
    api.post("/categories", {
      name: sanitizeInput(categoryData.name),
      description: sanitizeInput(categoryData.description),
    }),
  updateCategory: (id, categoryData) =>
    api.put(`/categories/${id}`, {
      name: sanitizeInput(categoryData.name),
      description: sanitizeInput(categoryData.description),
    }),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
};

export const listAPI = {
  getPublicLists: (page = 0, size = 20, sortBy = "recent") =>
    api.get(`/lists/public?page=${page}&size=${size}&sortBy=${sortBy}`),
  getListsByCategory: (categoryId, page = 0, size = 20, sortBy = "recent") =>
    api.get(
      `/lists/public/category/${categoryId}?page=${page}&size=${size}&sortBy=${sortBy}`,
    ),
  searchLists: (query, page = 0, size = 20) =>
    api.get(
      `/lists/search?query=${encodeURIComponent(
        sanitizeInput(query),
      )}&page=${page}&size=${size}`,
    ),
  getListDetail: (id) => api.get(`/lists/public/${id}`),
  getListDetailBySlug: (slug) => api.get(`/lists/public/slug/${slug}`),
  createList: (listData) =>
    api.post("/lists", {
      ...listData,
      title: sanitizeInput(listData.title),
      description: sanitizeInput(listData.description),
    }),
  updateList: (id, listData) =>
    api.put(`/lists/${id}`, {
      ...listData,
      title: sanitizeInput(listData.title),
      description: sanitizeInput(listData.description),
    }),
  deleteList: (id) => api.delete(`/lists/${id}`),
  getAllListsAdmin: (page = 0, size = 20) =>
    api.get(`/lists/admin/all?page=${page}&size=${size}`),
  searchListsAdmin: (query, page = 0, size = 100) =>
    api.get(
      `/lists/admin/search?query=${encodeURIComponent(
        sanitizeInput(query),
      )}&page=${page}&size=${size}`,
    ),
};

export const itemAPI = {
  getItemsByListId: (listId) => api.get(`/items/list/${listId}`),
  addItemToList: (listId, itemData) =>
    api.post(`/items/list/${listId}`, {
      answer: sanitizeInput(itemData.answer),
      rankPosition: itemData.rankPosition,
    }),
  updateItem: (itemId, itemData) =>
    api.put(`/items/${itemId}`, {
      answer: sanitizeInput(itemData.answer),
      rankPosition: itemData.rankPosition,
    }),
  deleteItem: (itemId) => api.delete(`/items/${itemId}`),
  deleteAllItems: (listId) => api.delete(`/items/list/${listId}/all`),
};

export const guessAPI = {
  submitGuess: (listId, guess, difficultyMode, isTimed) =>
    api.post(`/guess/${listId}`, {
      guess,
      difficultyMode,
      isTimed,
    }),
  resetProgress: (listId) => api.delete(`/guess/${listId}/reset`),
  abortTimedSession: (listId) =>
    api.post(`/guess/${listId}/abort-timed-session`),
  abortTimedMode: (listId) => api.post(`/guess/${listId}/abort-timed`),
  updateModeSelection: (listId, difficultyMode, isTimed) =>
    api.post(`/guess/${listId}/update-mode`, {
      difficultyMode,
      isTimed,
    }),
};

export const progressAPI = {
  getUserProgress: () => api.get("/progress"),
  getInProgressLists: () => api.get("/progress/in-progress"),
  getCompletedLists: () => api.get("/progress/completed"),
  getProgressForList: (listId) => api.get(`/progress/list/${listId}`),
  resetBestCasualGuesses: (listId) =>
    api.delete(`/progress/list/${listId}/reset-best-casual-guesses`),
  resetBestRelaxedGuesses: (listId) =>
    api.delete(`/progress/list/${listId}/reset-best-relaxed-guesses`),
  resetBestCasualTimed: (listId) =>
    api.delete(`/progress/list/${listId}/reset-best-casual-timed`),
  resetBestRelaxedTimed: (listId) =>
    api.delete(`/progress/list/${listId}/reset-best-relaxed-timed`),
};

export const analyticsAPI = {
  getOverallAnalytics: () => api.get("/admin/analytics/overview"),
  getListAnalytics: (listId) => api.get(`/admin/analytics/list/${listId}`),
  getAllListsAnalytics: () => api.get("/admin/analytics/lists/all"),
  getAllUsers: () => api.get("/admin/users"),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
};

export default api;
